const resources = [
  {
    icon: 'ri-test-tube-line',
    title: 'Genetic Testing',
    description:
      'Whole exome sequencing (WES) of the MAP3K7 gene is the only way to confirm a CSCF diagnosis. Without it, children may be misdiagnosed or miss critical monitoring and care.',
    link: '#genetic-testing',
    color: 'bg-emerald-50 border-emerald-200',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    tag: 'Diagnosis',
    highlight: true,
  },
  {
    icon: 'ri-seedling-line',
    title: 'Early Intervention',
    description:
      'Early therapy services — occupational, physical, and speech — can dramatically improve developmental outcomes for children with CSCF. Connect with early intervention programs in your area.',
    link: '#community',
    color: 'bg-emerald-50 border-emerald-100',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    tag: 'Developmental',
    highlight: false,
  },
  {
    icon: 'ri-volume-mute-line',
    title: 'Hearing Support',
    description:
      'Most children with CSCF have conductive hearing loss. Audiologists, hearing aids, and cochlear implant teams can provide critical support for communication and learning.',
    link: '#community',
    color: 'bg-accent-50 border-accent-100',
    iconBg: 'bg-accent-100',
    iconColor: 'text-accent-600',
    tag: 'Audiology',
    highlight: false,
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Cardiology Care',
    description:
      'Cardiac involvement is a hallmark of CSCF. Finding a pediatric cardiologist experienced with complex congenital heart disease and rare syndromes is essential.',
    link: '#community',
    color: 'bg-red-50 border-red-100',
    iconBg: 'bg-red-100',
    iconColor: 'text-red-500',
    tag: 'Cardiology',
    highlight: false,
  },
  {
    icon: 'ri-school-line',
    title: 'School Support',
    description:
      'Children with CSCF may qualify for IEPs, 504 plans, and specialized services. Knowing your child\'s educational rights is a powerful tool for advocacy.',
    link: '#community',
    color: 'bg-amber-50 border-amber-100',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    tag: 'Education',
    highlight: false,
  },
];

export default function Resources() {
  return (
    <section id="resources" className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-block px-4 py-1.5 bg-brand-50 border border-brand-100 rounded-full mb-4">
            <span className="text-xs font-semibold text-brand-600 tracking-widest font-heading">RESOURCES</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">
            Support Across Every Area
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto font-body leading-relaxed">
            CSCF affects multiple body systems. Here are the key areas where families most often need guidance and connection.
          </p>
        </div>

        {/* Genetic testing featured banner */}
        {resources.filter(r => r.highlight).map((r) => (
          <div
            key={r.title}
            className="mb-6 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-emerald-100 rounded-xl flex-shrink-0">
              <i className={`${r.icon} text-2xl ${r.iconColor}`}></i>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-bold text-emerald-600 uppercase tracking-widest font-heading">{r.tag}</span>
                <span className="text-xs bg-emerald-200 text-emerald-800 font-semibold px-2 py-0.5 rounded-full font-body">Essential first step</span>
              </div>
              <h3 className="text-base font-bold text-gray-900 font-heading mb-1">{r.title}</h3>
              <p className="text-sm text-gray-600 font-body leading-relaxed">{r.description}</p>
            </div>
            <a
              href={r.link}
              className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white font-semibold rounded-full hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer font-body text-sm"
            >
              Learn more <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        ))}

        {/* 4-item resource grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {resources.filter(r => !r.highlight).map((r) => (
            <div
              key={r.title}
              className={`rounded-2xl border p-6 flex flex-col ${r.color} hover:scale-[1.02] transition-transform duration-200`}
            >
              {/* Tag */}
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4 font-heading">
                {r.tag}
              </span>
              {/* Icon */}
              <div className={`w-12 h-12 flex items-center justify-center ${r.iconBg} rounded-xl mb-4`}>
                <i className={`${r.icon} text-2xl ${r.iconColor}`}></i>
              </div>
              <h3 className="text-base font-bold text-gray-900 mb-3 font-heading">{r.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed flex-grow font-body">{r.description}</p>
              <a
                href={r.link}
                className="inline-flex items-center gap-1 mt-5 text-sm font-semibold text-gray-700 hover:text-brand-600 transition-colors cursor-pointer font-body"
              >
                Get connected <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 text-sm font-body mb-4">
            Need help finding the right specialist or resource?
          </p>
          <a
            href="#community"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-500 text-white font-semibold rounded-full hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer font-body"
          >
            <i className="ri-customer-service-2-line"></i>
            Ask the Community
          </a>
        </div>
      </div>
    </section>
  );
}
